import React from "react";
import styled from "styled-components";
import { Star } from "lucide-react";
import { formatDate } from "@shared/utils/date";
import {
  LanguageNameSpaces,
  SortingOrder,
  SubGroupReview,
} from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import SortSelect from "common/Select/SortSelect";
import Panel from "common/NewLook/Panel.tsx";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";

const SORT_VALUES = ["Date", "Rating"];
const MAX_STARS = 5;

interface NewLookReviewsListProps {
  reviews: SubGroupReview[];
  sortingBy: string;
  sortOrder: SortingOrder;
  setSortingBy: (sortingBy: string) => void;
  setSortOrder: (sortOrder: SortingOrder) => void;
}

/** Five small stars, filled up to the rating. */
export const Stars = ({ value }: { value: number }) => (
  <StarsRow aria-label={`${value}/${MAX_STARS}`}>
    {Array.from({ length: MAX_STARS }).map((_, index) => (
      <Star
        key={index}
        size={13}
        strokeWidth={0}
        fill="currentColor"
        data-filled={index < Math.round(value) || undefined}
      />
    ))}
  </StarsRow>
);

const getInitials = (name?: string) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

/**
 * The reviews as a panel: the count in the header with the sort control, then
 * a hairline list (initials, name, date, stars, message).
 */
const NewLookReviewsList = ({
  reviews,
  sortingBy,
  sortOrder,
  setSortingBy,
  setSortOrder,
}: NewLookReviewsListProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);

  return (
    <Panel
      title={t("drawer.tabs.reviews")}
      count={reviews.length}
      flush={true}
      actions={
        <SortSelect
          sortBy={sortingBy}
          setSortBy={setSortingBy}
          sortDirection={sortOrder}
          setSortDirection={setSortOrder}
          sortValues={SORT_VALUES}
          labelMapper={(value) =>
            value === "Rating" ? t("reviews.rating_column") : t("reviews.date")
          }
        />
      }
    >
      <List>
        {reviews.map((review, index) => (
          <Item key={index}>
            <Avatar aria-hidden="true">{getInitials(review.full_name)}</Avatar>
            <Body>
              <Top>
                <Name>{review.full_name}</Name>
                <Stars value={review.stars} />
              </Top>
              <Meta>{formatDate(review.creation_timestamp, false, true)}</Meta>
              {!!review.message && <Message>{review.message}</Message>}
            </Body>
          </Item>
        ))}
      </List>
    </Panel>
  );
};

export default NewLookReviewsList;

const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;
const Item = styled.li`
  display: flex;
  gap: 12px;
  padding: 16px 20px;
  & + & {
    border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
`;
const Avatar = styled.span`
  flex: none;
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${(p) => getNewLookTokens(p.theme).well};
  box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  ${newLookType.meta};
  font-weight: 500;
`;
const Body = styled.div`
  flex: 1;
  min-width: 0;
`;
const Top = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;
const Name = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const Meta = styled.div`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const Message = styled.p`
  margin: 8px 0 0;
  ${newLookType.body};
  color: ${(p) => getNewLookTokens(p.theme).text};
  white-space: pre-line;
  overflow-wrap: anywhere;
`;
const StarsRow = styled.span`
  flex: none;
  display: inline-flex;
  gap: 2px;
  color: ${(p) => getNewLookTokens(p.theme).hairlineStrong};
  svg[data-filled] {
    color: ${(p) => p.theme.colors.yellow};
  }
`;
